const normalizeId = (id) => {
  if (id == null) return '';
  if (typeof id === 'object') {
    if (id._id != null) return String(id._id);
    if (id.id != null) return String(id.id);
  }
  return String(id);
};

const getUserId = (user) => normalizeId(user?._id ?? user?.id);

const getUserCompanyId = (user) =>
  normalizeId(user?.company?._id ?? user?.company ?? user?.companyId);

const personMatchesUser = (person, user) => {
  if (!person || !user) return false;
  const userId = getUserId(user);
  const personUserId = normalizeId(person.user);
  if (userId && personUserId && userId === personUserId) return true;
  const userEmail = (user.email || '').trim().toLowerCase();
  const personEmail = (person.email || '').trim().toLowerCase();
  return Boolean(userEmail && personEmail && userEmail === personEmail);
};

const staffListMatchesUser = (list, user) =>
  (list || []).some((person) => personMatchesUser(person, user));

const engineerMatchesUser = (managers, user) =>
  (managers || []).some((manager) =>
    (manager.engineers || []).some((eng) => personMatchesUser(eng, user))
  );

/** True when the user is assigned on this bridge as creator/admin, owner, manager, or engineer. */
export const isAssignedOperationalStaff = (bridge, user) => {
  if (!bridge || !user) return false;
  const userId = getUserId(user);
  const createdById = normalizeId(bridge.createdBy);
  return (
    Boolean(userId && createdById && userId === createdById) ||
    staffListMatchesUser(bridge.admins, user) ||
    staffListMatchesUser(bridge.owners, user) ||
    staffListMatchesUser(bridge.managers, user) ||
    engineerMatchesUser(bridge.managers, user)
  );
};

/**
 * Drop bridges that belong to a different company than the logged-in user.
 * super_admin: unchanged. Users without a company: unchanged (role filter still applies).
 * Legacy bridges with no company stay visible so existing assignments keep working.
 */
export const filterBridgesByCompany = (bridges, user) => {
  if (!user?.role || user.role === 'super_admin') return bridges || [];
  const companyKey = getUserCompanyId(user);
  if (!companyKey) return bridges || [];

  return (bridges || []).filter((bridge) => {
    const bridgeCompany = normalizeId(bridge.company);
    if (!bridgeCompany) return true;
    return bridgeCompany === companyKey;
  });
};

export const filterBridgesByRole = (bridges, user) => {
  const scoped = filterBridgesByCompany(bridges, user);
  if (!user?.role || user.role === 'super_admin') return scoped;

  const userId = getUserId(user);

  switch (user.role) {
    case 'bridge_admin':
      return scoped.filter(
        (bridge) =>
          normalizeId(bridge.createdBy) === userId ||
          staffListMatchesUser(bridge.admins, user)
      );
    case 'bridge_owner':
      return scoped.filter((bridge) => staffListMatchesUser(bridge.owners, user));
    case 'bridge_manager':
    case 'project_manager':
      return scoped.filter((bridge) => staffListMatchesUser(bridge.managers, user));
    case 'bridge_engineer':
    case 'project_engineer':
      return scoped.filter((bridge) => engineerMatchesUser(bridge.managers, user));
    default:
      return scoped;
  }
};

/** Map: admins see peer bridges in their company. Managers see only the bridges in their list. */
export const filterBridgesForMap = (bridges, user) => {
  const role = user?.role;
  if (!role || role === 'super_admin' || role === 'bridge_admin') {
    return filterBridgesByCompany(bridges, user);
  }
  return filterBridgesByRole(bridges, user);
};

const collectBridgeIdentities = (bridge) => {
  const ids = new Set();
  const emails = new Set();
  const push = (person) => {
    if (!person) return;
    const id = normalizeId(person.user);
    const email = (person.email || '').trim().toLowerCase();
    if (id) ids.add(id);
    if (email) emails.add(email);
  };
  (bridge.admins || []).forEach(push);
  (bridge.owners || []).forEach(push);
  (bridge.managers || []).forEach((manager) => {
    push(manager);
    (manager.engineers || []).forEach(push);
  });
  const createdBy = normalizeId(bridge.createdBy);
  if (createdBy) ids.add(createdBy);
  return { ids, emails };
};

/**
 * Bridges belonging to a company's admin group.
 * Starts from the given admin(s), then transitively includes every bridge that
 * shares a creator/admin/owner/manager/engineer identity with an already-included
 * bridge, so all bridges under the same admin team are shown (optionally scoped to
 * a company).
 */
export const filterBridgesByAdmin = (bridges, admins, companyId = null) => {
  if (!bridges?.length) return [];

  const seedAdmins = (Array.isArray(admins) ? admins : [admins]).filter(Boolean);
  if (!seedAdmins.length) return [];

  const companyKey = companyId != null ? normalizeId(companyId) : '';

  const scoped = bridges.filter((bridge) => {
    if (!companyKey) return true;
    const bridgeCompany = normalizeId(bridge.company);
    return !bridgeCompany || bridgeCompany === companyKey;
  });

  const knownIds = new Set();
  const knownEmails = new Set();
  seedAdmins.forEach((admin) => {
    const id = getUserId(admin);
    const email = (admin.email || '').trim().toLowerCase();
    if (id) knownIds.add(id);
    if (email) knownEmails.add(email);
  });

  const included = new Map();
  let changed = true;

  while (changed) {
    changed = false;
    for (const bridge of scoped) {
      const key = String(bridge._id);
      if (included.has(key)) continue;

      const { ids, emails } = collectBridgeIdentities(bridge);
      const shares =
        [...ids].some((id) => knownIds.has(id)) ||
        [...emails].some((email) => knownEmails.has(email));

      if (shares) {
        included.set(key, bridge);
        ids.forEach((id) => knownIds.add(id));
        emails.forEach((email) => knownEmails.add(email));
        changed = true;
      }
    }
  }

  return Array.from(included.values());
};
